import asHTML from "../template";
import Ssss from "../ssss";
import _d from "../debug";

import {
  clamp,
  isApprox,
  type OnStateChange,
  type Player,
  type TrackId,
} from "./lib";

function d(lvl: number, ...args: any[]) {
  _d(lvl, "YOUTUBE.ts: ", ...args);
}

declare global {
  interface Window {
    onYouTubeIframeAPIReady: () => void;
  }
}

const domPlayer = document.getElementById("player") as HTMLDivElement;
const domPlayerInfo = document.getElementById("player-info") as HTMLDivElement;
const templates = {
  playerDiv: asHTML<HTMLDivElement>`<div id="youtube-player"></div>`,
  title: (t: string) =>
    asHTML<HTMLDivElement>`
    <div id="youtube-info">
      <span id="track-title">${t}</span>
    </div>
  `,
  speeds: asHTML<HTMLDivElement>`
    <div id="youtube-speed">
      Speed: <span id="youtube-speed-val">1.0</span>
      <button id="youtube-speed-down">speed down</button>
      <button id="youtube-speed-up">speed up</button>
    </div>
`,
};

type YoutubePlayerState = {
  n: YT.PlayerState;
  title: string;
  rate: number;
};

const updateRate = (player: YT.Player, inc: number) => {
  d(1, "updating rate", inc);
  const rates = player.getAvailablePlaybackRates();
  const rateIdx = rates.findIndex((val) => val === player.getPlaybackRate());
  if (rateIdx < 0) {
    // ??? give up for now
    d(0, "youtube playback rate not found?");
    return;
  } else {
    const newRateIdx = clamp(rateIdx + inc, {
      start: 0,
      end: rates.length - 1,
    });
    player.setPlaybackRate(rates[newRateIdx]);
  }
};

export async function initialize(
  videoId: string,
  speedContainerEl: HTMLElement,
): Promise<Player> {
  console.log("INITIALIZE CALLED");

  speedContainerEl.replaceChildren(templates.speeds);
  injectYoutube();
  let player: YT.Player | undefined;

  const s4 = Ssss<YoutubePlayerState>({ n: -1, title: "" });

  const onStateChange: { fn: null | OnStateChange } = { fn: null };
  function registerStateChange(fn: OnStateChange) {
    onStateChange.fn = fn;
  }

  function onPlayerStateChange(evt: YT.OnStateChangeEvent) {
    d(10, "onPlayerStateChange", evt.data);
    s4.update({
      title:
        document.querySelector<HTMLIFrameElement>("#youtube-player")?.title ??
        "",
    });
    s4.mod("n", evt.data);
  }

  s4.sub("title", (prev) => {
    const title = s4.get("title");
    if (title !== prev.title && title !== "YouTube video player") {
      domPlayerInfo.replaceChildren(templates.title(title));
    }
  });

  window.onYouTubeIframeAPIReady = async function () {
    console.log("READY");
    player = new YT.Player("youtube-player", {
      height: "200",
      width: "200",
      videoId,
      playerVars: {
        playsinline: 1,
      },
      events: {
        onStateChange: onPlayerStateChange,
        onError: (e) => {
          if (onStateChange.fn) {
            onStateChange.fn({ trackError: e });
          }
        },
        onPlaybackRateChange: (e) => {
          document.getElementById("youtube-speed-val")!.innerHTML = e.data + "";
        },
      },
    });

    document
      .getElementById("youtube-speed-down")!
      .addEventListener("click", () => {
        updateRate(player!, -1);
      });
    document
      .getElementById("youtube-speed-up")!
      .addEventListener("click", () => {
        updateRate(player!, 1);
      });

    await s4.waitFor("n", (_, cur) => cur !== YT.PlayerState.UNSTARTED);

    updatePosition(player, onStateChange);
  };

  function load(track: string) {
    player?.pauseVideo();
    player?.loadVideoById(track);
  }

  function validate(input: string): TrackId | null {
    const match = input.match(/.*(?:\/|v=|^)([a-zA-Z0-9-_]{11})/i);
    return match?.[1] ?? null;
  }

  // set up youtube thingy

  return {
    seek: async (ms: number) => {
      d(10, "seeking to ", ms);
      player?.seekTo((ms + 2) / 1000, true);
      return new Promise((resolve) => {
        let interval = setInterval(() => {
          // within 10ms might be ok...
          const msTime = Math.round(player!.getCurrentTime() * 1000);
          if (isApprox(msTime, ms, 10)) {
            clearInterval(interval);
            // make sure to update before resolving! this way, the promise
            // returns only after all updates for the new time have been run.
            report(player!, onStateChange);
            resolve();
          }
        }, 0);
      });
    },
    next: null,
    prev: null,
    pause: async () => player?.pauseVideo(),
    togglePlay: async () => {
      if (!player) return;
      if (s4.get("n") === YT.PlayerState.BUFFERING) {
        await s4.waitFor("n", (_, cur) => {
          return cur !== YT.PlayerState.BUFFERING;
        });
      }
      const n = s4.get("n");
      if (n === YT.PlayerState.PLAYING) {
        player.pauseVideo();
      } else {
        player.playVideo();
      }
    },
    mute: () => {
      player!.mute();
    },
    unmute: () => {
      player!.unMute();
    },
    onStateChange: registerStateChange,
    load,
    validate,
  };
}

function injectYoutube() {
  domPlayer.appendChild(templates.playerDiv);
  const ytInjection = document.createElement("script");
  ytInjection.src = "https://www.youtube.com/iframe_api";
  document.body.insertBefore(ytInjection, document.body.children[0]);
}

function report(
  player: YT.Player,
  onStateChange: { fn: OnStateChange | null },
) {
  const dur = player.getDuration();
  if (typeof dur === "number" && dur > 0) {
    const updates = {
      position: Math.floor(player.getCurrentTime() * 1000),
      duration: Math.floor(dur * 1000),
      playing: player.getPlayerState() === YT.PlayerState.PLAYING,
      muted: player.isMuted(),
    };
    onStateChange.fn?.(updates);
  }
}

function updatePosition(
  player: YT.Player,
  onStateChange: { fn: OnStateChange | null },
) {
  report(player, onStateChange);
  requestAnimationFrame(updatePosition.bind(null, player, onStateChange));
}
